import {
  AnalyticsEvent,
  ConfigApi,
  DiscoveryApi,
  ErrorApi,
  IdentityApi,
} from '@backstage/core-plugin-api';
import { CatalogApi } from '@backstage/catalog-client';
import { Entity } from '@backstage/catalog-model';

type AnalyticsAPI = {
  captureEvent: (event: AnalyticsEvent) => void;
};

type Options = {
  configApi: ConfigApi;
  errorApi: ErrorApi;
  identityApi: IdentityApi;
  catalogApi: CatalogApi;
  discoveryApi: DiscoveryApi;
};

export class DBAnalyticsAPI implements AnalyticsAPI {
  private readonly configApi: ConfigApi;
  private readonly catalogApi: CatalogApi;
  private readonly errorApi: ErrorApi;
  private readonly discoveryApi: DiscoveryApi;
  private readonly identityApi: IdentityApi;
  private sessionId: string | undefined;
  private eventQueue: {
    event: AnalyticsEvent;
    timestamp: Date;
    user?: string;
    teamMetadata?: Entity;
    sessionId?: string;
  }[] = [];
  private flushInterval: number;
  private retryLimit: number = 3;
  private eventRetryCounter: Map<string, number> = new Map();
  private debug: boolean;

  constructor(options: Options) {
    this.configApi = options.configApi;
    this.errorApi = options.errorApi;
    this.catalogApi = options.catalogApi;
    this.identityApi = options.identityApi;
    this.discoveryApi = options.discoveryApi;

    this.debug =
      this.configApi.getOptionalBoolean('analyticsDb.debug') === true;
    if (this.debug) {
      console.log('Debug mode is enabled.');
    }
    const configFlushIntervalMinutes = this.configApi.getOptionalNumber(
      'analyticsDb.interval',
    );

    this.flushInterval =
      configFlushIntervalMinutes !== null &&
      configFlushIntervalMinutes !== undefined
        ? configFlushIntervalMinutes * 60 * 1000
        : 30 * 60 * 1000; // Default to 30 minutes if not specified

    if (this.flushInterval === 0) {
      this.captureEvent = this.instantCaptureEvent;
    } else {
      this.startFlushCycle();
    }
  }

  static fromConfig(
    config: ConfigApi,
    errorApi: ErrorApi,
    identityApi: IdentityApi,
    catalogApi: CatalogApi,
    discoveryApi: DiscoveryApi,
  ) {
    return new DBAnalyticsAPI({
      configApi: config,
      errorApi: errorApi,
      identityApi,
      catalogApi,
      discoveryApi,
    });
  }

  private async callApi<T>(
    path: string,
    options?: { method?: string; body?: any },
  ): Promise<T> {
    const apiUrl = await this.discoveryApi.getBaseUrl(`analytics-db`);
    const { token } = await this.identityApi.getCredentials();
    const headers: HeadersInit = {
      Accept: 'application/json',
      Authorization: `Bearer ${token}`,
    };

    if (options?.body) {
      headers['Content-Type'] = 'application/json';
    }

    const response = await fetch(`${apiUrl}/${path}`, {
      method: options?.method ?? 'GET',
      headers,
      body: options?.body
        ? JSON.stringify({ events: options.body })
        : undefined,
    });

    if (!response.ok) {
      throw new Error(
        `Request failed for ${path}, ${response.status} ${response.statusText}`,
      );
    }

    return (await response.json()) as T;
  }

  private generateSessionId(): string {
    return Math.random().toString(36).substring(2) + Date.now().toString(36);
  }

  private readSessionIdFromCookie(): string | undefined {
    const name = 'sessionId=';
    const decodedCookie = decodeURIComponent(document.cookie);
    const ca = decodedCookie.split(';');
    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) === ' ') {
        c = c.substring(1);
      }
      if (c.indexOf(name) === 0) {
        return c.substring(name.length, c.length);
      }
    }
    return undefined;
  }

  private log(message: string, isError: boolean = false): void {
    if (this.debug) {
      if (isError) {
        console.error(message);
      } else {
        console.log(message);
      }
    }
  }

  async captureEvent(event: AnalyticsEvent) {
    this.log(`captureEvent called with event: ${JSON.stringify(event)}`);
    this.sessionId = this.readSessionIdFromCookie();
    if (!this.sessionId) {
      this.sessionId = this.generateSessionId();
      document.cookie = `sessionId=${this.sessionId}; path=/`;
      this.log(`Generated and set sessionId: ${this.sessionId}`);
    }
    const user = await this.getUser();
    if (!user) {
      this.log('Error: user is undefined.');
      return;
    }

    const teamMetadata = await this.catalogApi.getEntityByRef(user);

    this.log(
      `Capturing event: ${JSON.stringify(
        event,
      )} User ID: ${user} Team Metadata: ${JSON.stringify(
        teamMetadata,
      )} Session ID: ${this.sessionId}`,
    );

    this.eventQueue.push({
      event,
      timestamp: new Date(),
      user: user,
      teamMetadata: teamMetadata,
      sessionId: this.sessionId,
    });

    if (this.flushInterval === 0) {
      const eventToFlush = this.eventQueue.pop();
      if (eventToFlush) {
        this.flushEvents([eventToFlush]);
      }
    }
  }

  private async getUser(): Promise<string | undefined> {
    this.log(`getUser called`);
    try {
      if (this.identityApi) {
        const identity = await this.identityApi.getBackstageIdentity();
        this.log(`Identity: ${JSON.stringify(identity)}`);
        return identity?.userEntityRef;
      }
    } catch (error) {
      this.log(`Failed to get user identity: ${error}`, true);
    }
    return undefined;
  }

  private async instantCaptureEvent(event: AnalyticsEvent) {
    this.log(`instantCaptureEvent called with event: ${JSON.stringify(event)}`);
    this.sessionId = this.readSessionIdFromCookie();
    if (!this.sessionId) {
      this.sessionId = this.generateSessionId();
      document.cookie = `sessionId=${this.sessionId}; path=/`;
      this.log(`Generated and set sessionId: ${this.sessionId}`);
    }
    const user = await this.getUser();
    if (!user) {
      this.log('Error: user is undefined.');
      return;
    }

    const teamMetadata = await this.catalogApi.getEntityByRef(user);

    this.log(
      `Capturing event: ${JSON.stringify(
        event,
      )} User ID: ${user} Team Metadata: ${JSON.stringify(
        teamMetadata,
      )} Session ID: ${this.sessionId}`,
    );

    const eventWithTimestamp = {
      event,
      timestamp: new Date(),
      user: user,
      teamMetadata,
      sessionId: this.sessionId,
    };

    await this.flushEvents([eventWithTimestamp]);
  }

  private startFlushCycle() {
    this.log(`startFlushCycle called`);
    setInterval(async () => {
      if (this.eventQueue.length > 0) {
        this.flushEvents(this.eventQueue.splice(0));
      }
    }, this.flushInterval);
    this.log(`Starting flush cycle with interval: ${this.flushInterval}ms`);
  }

  private async flushEvents(
    events: {
      event: AnalyticsEvent;
      timestamp: Date;
      user?: string;
      teamMetadata?: Entity;
      sessionId?: string;
    }[],
  ) {
    this.log(`flushEvents called with ${events.length} events`);
    if (events.length === 0) {
      this.log('No events to flush.');
      return;
    }

    this.log(`Flushing ${events.length} events`);

    try {
      await this.callApi('create-events', {
        method: 'POST',
        body: events,
      });

      this.log('Successfully flushed events.');
    } catch (error) {
      this.log(`Failed to flush analytics events: ${error}`, true);
      this.errorApi.post(
        new Error(`Failed to flush analytics events: ${error}`),
      );

      events.forEach(event => {
        const eventId = JSON.stringify(event);
        const retries = this.eventRetryCounter.get(eventId) || 0;
        if (retries < this.retryLimit) {
          this.eventQueue.push(event);
          this.eventRetryCounter.set(eventId, retries + 1);
          this.log(`Retrying event: ${eventId}, attempt ${retries + 1}`, true);
        } else {
          this.log(`Max retries reached for event: ${eventId}`, true);
          this.errorApi.post(
            new Error(`Max retries reached for event: ${eventId}`),
          );
        }
      });
    }
  }
}
